import React, { useState } from 'react'

import { Volume2, Info } from 'lucide-react'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { FlagDTO } from '@/core/api/dtos/response/zone/flag'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Schedule } from '@/core/api/entities/schedule'
import { Occupation } from '@/core/api/entities/zone/occupation'
import { ZoneStateStatus } from '@/core/api/entities/zone/state'
import { Zone } from '@/core/api/entities/zone/zone'
import { ZoneConnection } from '@/core/api/entities/zone/zoneConnection'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { AlertsBadges } from '@/features/zones/components/AlertsBadges'
import { ConnectionChart } from '@/features/zones/components/ConnectionChart'
import { OccupationBarChart } from '@/features/zones/components/OccupationBarChart'
import { ChangeAdvertAudioVersionModal } from '@/features/zones/modals/ChangeAdvertAudioVersionModal'
import { ChangeVolumeModal } from '@/features/zones/modals/ChangeVolumeModal'
import {
	getHasSmartIntegration,
	transformConnectionData,
} from '@/features/zones/utils/common'
import { Button } from '@/shared/components/atoms/Button'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Switch } from '@/shared/components/atoms/Switch'
import Tooltip from '@/shared/components/atoms/Tooltip'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetDescription,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { AdCampaignsSection } from '../components/AdCampaignsSection'
import { AssignedGroups } from '../components/AssignedGroupsSection'
import { DeleteSection } from '../components/DeleteSection'
import { PlaybackSection } from '../components/PlaybackSection'
import { PlaylistsSection } from '../components/PlaylistsSection'
import { SchedulesSection } from '../components/SchedulesSection'
import { ZoneInfoHeader } from '../components/ZoneInfoHeader'

interface InformationTabProps {
	zone: Zone
	connections: ZoneConnection[]
	hasBoxControls: boolean
	isUpdating: boolean
	hasUpdateError?: boolean
	isConnectionsLoading?: boolean
	occupationData?: Occupation[]
	isLoadingOccupation?: boolean
	directPlaylists?: Playlist[]
	directSchedules?: Schedule[]
	isLoadingDirectPlaylists?: boolean
	onUpdateZone: (data: UpdateZoneRequestDTO) => void
	onDraftFlags?: (flags: string[]) => void
	onEditZone: () => void
	onPlaylistClick: (radioId: number) => void
	onCampaignClick: (campaignId: number) => void
	onAddPlaylist: () => void
	onAddSchedule: () => void
	onAddCampaign: () => void
	onRemoveCampaign: (campaignId: number) => void
	onAddGroup: () => void
	onRemoveGroup: (groupId: number) => void
	onRemovePlaylistFromZone?: (playlist: Playlist) => void
	onRemovePlaylistFromGroup?: (playlist: Playlist) => void
	onRemovePlaylistFromBoth?: (playlist: Playlist) => void
	onRemoveScheduleFromZone?: (schedule: Schedule) => void
	onRemoveScheduleFromGroup?: (schedule: Schedule) => void
	onRemoveScheduleFromBoth?: (schedule: Schedule) => void
	onDeleteZone: () => void
	onRefreshZone: () => void
	onChangeAdvertAudioVersion: (advertAudioVersionId: number) => void
	isUpdatingAdvertAudioVersion?: boolean
	hasAdvertAudioVersionFeature?: boolean
}

export const InformationTab: React.FC<InformationTabProps> = ({
	zone,
	connections,
	hasBoxControls: _hasBoxControls,
	isUpdating,
	hasUpdateError: _hasUpdateError = false,
	isConnectionsLoading = false,
	occupationData,
	isLoadingOccupation = false,
	directPlaylists = [],
	directSchedules = [],
	isLoadingDirectPlaylists = false,
	onUpdateZone,
	onDraftFlags,
	onEditZone,
	onPlaylistClick: _onPlaylistClick,
	onCampaignClick,
	onAddPlaylist,
	onAddSchedule,
	onAddCampaign,
	onRemoveCampaign,
	onAddGroup,
	onRemoveGroup,
	onRemovePlaylistFromZone,
	onRemovePlaylistFromGroup,
	onRemovePlaylistFromBoth,
	onRemoveScheduleFromZone,
	onRemoveScheduleFromGroup,
	onRemoveScheduleFromBoth,
	onDeleteZone,
	onRefreshZone,
	onChangeAdvertAudioVersion,
	isUpdatingAdvertAudioVersion = false,
	hasAdvertAudioVersionFeature = false,
}) => {
	const { t } = useTranslation('zones')
	const { hasAnyPermission } = usePermission()
	const { showNotification } = useNotificationContext()
	const [showVolumeModal, setShowVolumeModal] = useState(false)
	const [showAdvertAudioVersionModal, setShowAdvertAudioVersionModal] =
		useState(false)
	const [showConnectSheet, setShowConnectSheet] = useState(false)

	const assignedCampaigns = zone.getCampaignsAssigned()

	const canManageAnyPlaybackPermissions = hasAnyPermission([
		PERMISSIONS.UPDATE_PLAYERS_FLAG_SKIP_SONG,
		PERMISSIONS.UPDATE_PLAYERS_FLAG_PAUSE_SONG,
	])
	const canEditZone = hasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
		PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
		PERMISSIONS.UPDATE_USER_PLAYERS,
	])
	const canDeleteZone = hasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
		PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
		PERMISSIONS.DESTROY_USER_PLAYERS,
	])

	const canManageSkipPermission = hasAnyPermission([
		PERMISSIONS.UPDATE_PLAYERS_FLAG_SKIP_SONG,
	])
	const canManagePausePermission = hasAnyPermission([
		PERMISSIONS.UPDATE_PLAYERS_FLAG_PAUSE_SONG,
	])

	/** Playback flag keys we manage (FlagDTO.key from zone flags response) */
	const PLAYBACK_FLAG_KEYS: readonly FlagDTO['key'][] = [
		'PLAYER_SKIP_SONG',
		'PLAYER_PAUSE_SONG',
	]
	const flags = zone.flags || []
	const canSkipSongs = flags.includes('PLAYER_SKIP_SONG')
	const canPauseSongs = flags.includes('PLAYER_PAUSE_SONG')

	const getFlagsWithPlayback = (
		currentFlags: string[],
		playback: { skip: boolean; pause: boolean }
	) => {
		const otherFlags = currentFlags.filter(
			(f) => !PLAYBACK_FLAG_KEYS.includes(f as FlagDTO['key'])
		)
		const playbackFlags: FlagDTO['key'][] = []
		if (playback.skip) playbackFlags.push('PLAYER_SKIP_SONG')
		if (playback.pause) playbackFlags.push('PLAYER_PAUSE_SONG')
		return [...otherFlags, ...playbackFlags]
	}

	const applyDraftFlags = (nextFlags: string[]) => {
		onDraftFlags?.(nextFlags)
	}

	const groupAssignedSchedules = zone.getSchedulesAssigned()
	const groupAssignedPlaylists = zone.getPlaylistsAssigned()
	const allSchedules = [
		...directSchedules.map((s) => ({ schedule: s, source: 'direct' as const })),
		...groupAssignedSchedules
			.filter((gs) => !directSchedules.some((ds) => ds.id === gs.id))
			.map((s) => ({ schedule: s, source: 'group' as const })),
	]

	const hasSmartIntegration = getHasSmartIntegration(zone)

	const isZoneInstalled =
		zone.state?.status !== ZoneStateStatus.NOT_INSTALLED &&
		zone.state?.status !== ZoneStateStatus.NOT_REACTIVATED

	const integrationSourcePretty = zone.integrationSourcePretty

	const connectionData = connections
		? transformConnectionData(connections, 7)
		: []

	const handleVolumeChange = (data: UpdateZoneRequestDTO) => {
		onUpdateZone(data)
		setShowVolumeModal(false)
	}

	const handleAdvertAudioVersionChange = (advertAudioVersionId: number) => {
		onChangeAdvertAudioVersion(advertAudioVersionId)
		setShowAdvertAudioVersionModal(false)
	}

	const handleConnectZone = () => {
		setShowConnectSheet(true)
	}

	const handleConnectThisDevice = async () => {
		setShowConnectSheet(false)
		// TODO: Implement connect on this device flow
		if (zone.code) {
			try {
				await navigator.clipboard.writeText(zone.code)
			} catch (error) {
				console.error('Failed to copy zone code:', error)
			}
		}
		showNotification({
			type: 'success',
			message: zone.code
				? 'Código copiado. La zona se conectó con éxito.'
				: 'Zona conectada con éxito.',
		})
	}

	const handleConnectOtherDevice = async () => {
		setShowConnectSheet(false)
		// TODO: Implement connect on other device flow
		if (zone.code) {
			try {
				await navigator.clipboard.writeText(zone.code)
			} catch (error) {
				console.error('Failed to copy zone code:', error)
			}
		}
		showNotification({
			type: 'success',
			message: zone.code
				? 'Código copiado. La zona se conectó con éxito.'
				: 'Zona conectada con éxito.',
		})
	}

	const handleRemoveSchedule = (scheduleId: number) => {
		const selectedSchedule = allSchedules.find(
			({ schedule }) => schedule.id === scheduleId
		)
		if (!selectedSchedule) return
		if (selectedSchedule.source === 'direct') {
			const isAlsoInGroup = groupAssignedSchedules.some(
				(gs) => gs.id === scheduleId
			)
			if (isAlsoInGroup && onRemoveScheduleFromBoth) {
				onRemoveScheduleFromBoth(selectedSchedule.schedule)
				return
			}
			onRemoveScheduleFromZone?.(selectedSchedule.schedule)
			return
		}
		onRemoveScheduleFromGroup?.(selectedSchedule.schedule)
	}

	return (
		<>
			<div className="flex flex-col gap-6 pb-20 md:pb-0">
				<div className="order-1">
					<ZoneInfoHeader
						zone={zone}
						onEdit={canEditZone ? onEditZone : undefined}
						onConnectZone={handleConnectZone}
					/>

					{/* Playback permissions (draft: save with "Guardar cambios") */}
					{canManageAnyPlaybackPermissions && (
						<div className="flex flex-col gap-4 mb-6 md:mb-4 md:border md:rounded-xl md:px-6 md:py-5 md:bg-card-dark">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<p className="text-md font-normal">
										{t('details.playbackPermissions.title')}
									</p>
									<Tooltip
										title={t('details.playbackPermissions.permissionsInfo')}
										className="w-64"
									>
										<Info className="w-4 h-4 text-muted-foreground cursor-help shrink-0" />
									</Tooltip>
								</div>
							</div>

							<div className="flex flex-row gap-12">
								<div className="flex gap-2">
									<Label className="text-sm font-light">
										{t('details.playbackPermissions.canSkip')}
									</Label>
									<Switch
										checked={canSkipSongs}
										disabled={isUpdating || !canManageSkipPermission}
										onCheckedChange={(checked) => {
											if (!canManageSkipPermission || !onDraftFlags) return
											const next = getFlagsWithPlayback(flags, {
												skip: checked,
												pause: canPauseSongs,
											})
											applyDraftFlags(next)
										}}
									/>
								</div>
								<div className="flex gap-2">
									<Label className="text-sm font-light">
										{t('details.playbackPermissions.canPause')}
									</Label>
									<Switch
										checked={canPauseSongs}
										disabled={isUpdating || !canManagePausePermission}
										onCheckedChange={(checked) => {
											if (!canManagePausePermission || !onDraftFlags) return
											const next = getFlagsWithPlayback(flags, {
												skip: canSkipSongs,
												pause: checked,
											})
											applyDraftFlags(next)
										}}
									/>
								</div>
							</div>
						</div>
					)}
				</div>

				{zone.state?.warnings && zone.state.warnings.length > 0 && (
					<div className="order-2">
						<AlertsBadges
							warnings={zone.state.warnings}
							containerClassName="mb-6"
						/>
					</div>
				)}

				{zone.state?.isOnline() && zone.lastPlayedSchedule && (
					<div className="order-3 md:order-6">
						<PlaybackSection
							scheduleName={zone.lastPlayedSchedule?.title}
							scheduleImage={
								zone.lastPlayedSchedule?.metadata?.smallCovers?.[0]
							}
							isPlaying={!!zone.lastPlayedSchedule}
							onChangeVolume={() => setShowVolumeModal(true)}
							hasActiveBoxAllocation={zone.getActiveBoxAllocation() !== null}
						/>
					</div>
				)}

				<div className="order-4 md:order-4">
					<AssignedGroups
						title={t('details.assignedGroups.title', {
							count: zone.groups?.length || 0,
						})}
						items={zone.groups || []}
						onAdd={onAddGroup}
						onRemove={onRemoveGroup}
						addButtonText={t('details.assignedGroups.addButton')}
					/>
				</div>

				<div className="order-5 md:order-5">
					<ConnectionChart
						data={connectionData}
						mode="daily"
						showToggle={false}
						isLoading={isConnectionsLoading}
						variant="details"
					/>
				</div>

				{hasSmartIntegration && occupationData && (
					<div className="order-6 md:order-3">
						<OccupationBarChart
							occupationData={occupationData}
							isLoading={isLoadingOccupation}
							integrationSource={zone.integrationSource}
							integrationSourcePretty={integrationSourcePretty}
							showCard={true}
						/>
					</div>
				)}

				<div className="order-7">
					<PlaylistsSection
						directPlaylists={directPlaylists}
						groupPlaylists={groupAssignedPlaylists}
						onAdd={canEditZone ? onAddPlaylist : undefined}
						onRemoveFromZone={
							canEditZone ? onRemovePlaylistFromZone : undefined
						}
						onRemoveFromGroup={
							canEditZone ? onRemovePlaylistFromGroup : undefined
						}
						onRemoveFromBoth={
							canEditZone ? onRemovePlaylistFromBoth : undefined
						}
						isLoading={isLoadingDirectPlaylists}
					/>
				</div>

				<div className="order-8">
					<SchedulesSection
						schedules={allSchedules.map(({ schedule }) => schedule)}
						onAdd={canEditZone ? onAddSchedule : undefined}
						onRemove={canEditZone ? handleRemoveSchedule : undefined}
					/>
				</div>

				<div className="order-9">
					<AdCampaignsSection
						campaigns={assignedCampaigns}
						groups={zone.groups}
						variant="zone"
						onAdd={canEditZone ? onAddCampaign : undefined}
						onRemove={canEditZone ? onRemoveCampaign : undefined}
						onCampaignClick={onCampaignClick}
						subHeader={
							hasAdvertAudioVersionFeature ? (
								<div className="flex items-center justify-between py-4 px-6 bg-card-dark border border-border rounded-lg">
									<div>
										<p className="text-sm font-medium">
											{t('details.advertAudioVersion.label')}
										</p>
										{zone.advertAudioVersion && (
											<p className="text-xs text-muted-foreground mt-0.5">
												{t(
													`modals.changeAdvertAudioVersion.versions.${zone.advertAudioVersion.name}.label`,
													{ defaultValue: zone.advertAudioVersion.name }
												)}
											</p>
										)}
									</div>
									<Button
										type="button"
										variant="link"
										size="sm"
										onClick={() => setShowAdvertAudioVersionModal(true)}
										className="text-muted-foreground hover:text-foreground"
									>
										<Volume2 className="h-4 w-4" />
										{t('details.advertAudioVersion.changeAdvertVolumeButton')}
									</Button>
								</div>
							) : undefined
						}
					/>
				</div>

				{canDeleteZone && (
					<div className="hidden md:block order-10">
						<DeleteSection
							deleteText={t('details.actions.deleteZone')}
							onDelete={onDeleteZone}
							refreshText={
								isZoneInstalled ? t('details.actions.refreshCode') : undefined
							}
							onRefresh={isZoneInstalled ? onRefreshZone : undefined}
						/>
					</div>
				)}
			</div>

			<ChangeVolumeModal
				visible={showVolumeModal}
				zone={zone}
				onClose={() => setShowVolumeModal(false)}
				onConfirm={handleVolumeChange}
				isUpdating={isUpdating}
			/>

			<ChangeAdvertAudioVersionModal
				visible={showAdvertAudioVersionModal}
				zone={zone}
				onClose={() => setShowAdvertAudioVersionModal(false)}
				onConfirm={handleAdvertAudioVersionChange}
				isUpdating={isUpdatingAdvertAudioVersion}
			/>

			<BottomSheet open={showConnectSheet} onOpenChange={setShowConnectSheet}>
				<BottomSheetContent className="px-4 pb-6">
					<BottomSheetTitle className="sr-only">
						{t('modals.zoneOnboarding.step4.title')}
					</BottomSheetTitle>
					<BottomSheetDescription className="sr-only">
						{t('modals.zoneOnboarding.step4.description')}
					</BottomSheetDescription>
					<div className="space-y-3 pt-8 flex flex-col justify-center">
						<Button
							onClick={handleConnectThisDevice}
							className="w-full h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200"
						>
							{t('modals.zoneOnboarding.step4.connectThisDevice')}
						</Button>
						<Button
							onClick={handleConnectOtherDevice}
							variant="outline"
							className="w-full h-12 text-base font-medium rounded-full border-gray-600 text-white hover:bg-gray-800"
						>
							{t('modals.zoneOnboarding.step4.connectOtherDevice')}
						</Button>
					</div>
				</BottomSheetContent>
			</BottomSheet>
		</>
	)
}
